// ── 미팅 결과 입력·수정 창 (2026-11-03) ─────────────────────────────────────
// CallerPendingFeedback.jsx 의 결과 입력 창을 떼어 냈다 — 🔔 피드백 대기(입력)와 일일콜 목록 결과 ✎(수정·지우기)가 같이 쓴다.
//   mode='entry' : 새로 넣기(예전과 같은 모양)
//   mode='edit'  : 지금 결과·메모가 채워져 열리고 [결과 지우기](창 안에서 한 번 더 묻기)가 생긴다.
//                  '미팅인계'는 고를 수 없다 — 담당자 옮기기가 같이 필요하다(지금 결과가 미팅인계면 보여 주기만).
//   canEdit=false: 칩·버튼을 흐리게 막고 한 줄 안내(관리 화면에서 남의 콜 — 판정은 서버 keep-actions 의 feedback).
// 저장·지우기는 부르는 쪽이 한다(onSave(결과, 메모) / onDelete()).
import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';

const HANDOVER = '미팅인계';

export const feedbackChipStyle = (active) => ({
  padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700,
  border: `1.5px solid ${active ? '#2563eb' : 'var(--border)'}`,
  background: active ? 'rgba(37,99,235,0.12)' : 'var(--bg-card)',
  color: active ? '#2563eb' : 'var(--text)',
});
const blocked = (on) => (on ? null : { opacity: 0.45, cursor: 'not-allowed' });
const deleteBtnStyle = { ...feedbackChipStyle(false), color: '#dc2626', borderColor: '#fca5a5' };
const hintStyle = { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 10 };

export default function FeedbackEntryModal({ target, mode = 'entry', canEdit = true, saving = false, onClose, onSave, onDelete }) {
  const isEdit = mode === 'edit';
  const [options, setOptions] = useState(null); // 피드백 선택지 (열 때마다 다시 읽음)
  const [selected, setSelected] = useState('');
  const [memo, setMemo] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!target) return undefined;
    setSelected(isEdit ? String(target.feedback || '') : '');
    setMemo(isEdit ? String(target.feedback_memo || '') : '');
    setConfirmDelete(false);
    // 열 때마다 재조회 — 관리자가 설정에서 옵션을 바꿔도 반영되게 (2026-09-02).
    //   기존 options 는 응답 전까지 유지되므로 깜빡임 없음.
    let alive = true;
    apiClient.get('/api/tm/feedback/options').then((r) => { if (alive && r.ok) setOptions(r.data?.data || []); });
    return () => { alive = false; };
  }, [target, isEdit]);

  const shown = (options || []).filter((o) => !(isEdit && o.name === HANDOVER));
  const outside = isEdit && selected && options && !shown.some((o) => o.name === selected); // 미팅인계·지운 선택지
  const sub = [target?.visit_date, target?.visit_time].filter(Boolean).join(' ');
  const who = target?.tm_name || target?.sales_person_name;

  const footer = confirmDelete ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: 'var(--text-sub)', marginRight: 'auto' }}>결과를 지우면 다시 &apos;피드백 대기&apos;로 돌아가요. 지울까요?</span>
      <button type="button" style={feedbackChipStyle(false)} onClick={() => setConfirmDelete(false)}>아니요</button>
      <button type="button" style={{ ...deleteBtnStyle, ...blocked(!saving) }} disabled={saving} onClick={() => onDelete?.()}>
        {saving ? '지우는 중...' : '지우기'}
      </button>
    </div>
  ) : (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      {isEdit && (
        <button type="button" style={{ ...deleteBtnStyle, marginRight: 'auto', ...blocked(canEdit) }} disabled={!canEdit}
          onClick={() => setConfirmDelete(true)}>
          결과 지우기
        </button>
      )}
      <button type="button" style={feedbackChipStyle(false)} onClick={onClose}>취소</button>
      <button type="button" style={{ ...feedbackChipStyle(true), opacity: saving ? 0.6 : 1, ...blocked(canEdit) }}
        disabled={saving || !canEdit} onClick={() => onSave?.(selected, memo)}>
        {saving ? '저장 중...' : '저장'}
      </button>
    </div>
  );

  return (
    <Modal
      open={Boolean(target)}
      title={`${isEdit ? '결과 수정' : '결과 입력'} — ${target?.company_name || ''}`}
      icon="📝"
      onClose={onClose}
      width="min(440px, 96vw)"
      mobileSheet
      footer={footer}
    >
      {!canEdit && <div style={hintStyle}>결과는 본인 콜이거나 일일콜 관리 권한이 있어야 고칠 수 있어요.</div>}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        {sub} {who ? `· 담당 ${who}` : ''}
      </div>
      {outside && <div style={hintStyle}>지금 결과: {selected}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {shown.map((opt) => (
          <button key={opt.id || opt.name} type="button" disabled={!canEdit}
            style={{ ...feedbackChipStyle(selected === opt.name), ...blocked(canEdit) }} onClick={() => setSelected(opt.name)}>
            {opt.name}
          </button>
        ))}
        {options && shown.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>사용 가능한 피드백 선택지가 없습니다.</span>
        ) : null}
        {!options ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>선택지 불러오는 중...</span> : null}
      </div>
      <textarea
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        readOnly={!canEdit}
        placeholder="메모 (선택) — 마케터가 전달한 내용을 적어 주세요"
        rows={3}
        style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text)', resize: 'vertical' }}
      />
    </Modal>
  );
}
